<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import RequestContactInfoButton from '../RequestContactInfoButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { getPreferredVideoCaptureMimeType } from './mediaCaptureHelper';

export default {
  name: 'ReplyBottomPanel',
  components: {
    NextButton,
    FileUpload,
    VideoCallButton,
    RequestContactInfoButton,
  },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    audioInputDevices: {
      type: Array,
      default: () => [],
    },
    videoInputDevices: {
      type: Array,
      default: () => [],
    },
    hasCheckedAudioInputDevices: {
      type: Boolean,
      default: false,
    },
    hasCheckedVideoInputDevices: {
      type: Boolean,
      default: false,
    },
    selectedAudioInputDeviceId: {
      type: String,
      default: '',
    },
    selectedVideoInputDeviceId: {
      type: String,
      default: '',
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    onAudioInputDeviceChange: {
      type: Function,
      default: () => {},
    },
    onVideoInputDeviceChange: {
      type: Function,
      default: () => {},
    },
    onOpenCameraCapture: {
      type: Function,
      default: () => {},
    },
    onOpenLocationMessage: {
      type: Function,
      default: () => {},
    },
    onOpenStickerCatalog: {
      type: Function,
      default: () => {},
    },
    onOpenVideoCapture: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableWhatsAppFlows: {
      type: Boolean,
      default: false,
    },
    enableWhatsAppCommerce: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectWhatsappFlow',
    'selectWhatsappCommerce',
    'selectContentTemplate',
    'toggleQuotedReply',
    'requestContactInfoTemplate',
  ],
  setup(props) {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
    };
  },
  data() {
    return {
      ALLOWED_FILE_TYPES,
      showDeviceSettingsPanel: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      if (this.isEditorDisabled) return false;
      return this.showFileUpload || this.isNote;
    },
    canShowAudioRecorderControls() {
      if (this.isEditorDisabled) return false;
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    audioRecorderDisabled() {
      return this.hasCheckedAudioInputDevices && !this.audioInputDevices.length;
    },
    audioRecorderTooltip() {
      return this.audioRecorderDisabled
        ? this.$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_UNAVAILABLE')
        : this.$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON');
    },
    showAudioRecorderButton() {
      return this.canShowAudioRecorderControls;
    },
    canShowDeviceSettingsButton() {
      return (
        !this.isEditorDisabled &&
        (this.canShowAudioRecorderControls || this.showAttachButton)
      );
    },
    showCameraCaptureButton() {
      return !this.isEditorDisabled && this.showAttachButton;
    },
    showVideoCaptureButton() {
      return !this.isEditorDisabled && this.showAttachButton;
    },
    showLocationButton() {
      return (
        !this.isEditorDisabled &&
        this.isAWhatsAppChannel &&
        !this.isOnPrivateNote
      );
    },
    showStickerButton() {
      return (
        !this.isEditorDisabled &&
        this.isAWhatsAppChannel &&
        !this.isOnPrivateNote
      );
    },
    cameraCaptureDisabled() {
      return this.hasCheckedVideoInputDevices && !this.videoInputDevices.length;
    },
    cameraCaptureTooltip() {
      return this.cameraCaptureDisabled
        ? this.$t('CONVERSATION.REPLYBOX.CAMERA_UNAVAILABLE')
        : this.$t('CONVERSATION.REPLYBOX.TAKE_PHOTO');
    },
    videoCaptureDisabled() {
      return (
        (this.hasCheckedVideoInputDevices && !this.videoInputDevices.length) ||
        !this.preferredVideoCaptureMimeType
      );
    },
    videoCaptureTooltip() {
      if (!this.preferredVideoCaptureMimeType) {
        return this.$t('CONVERSATION.REPLYBOX.VIDEO_CAPTURE_UNSUPPORTED');
      }

      return this.cameraCaptureDisabled
        ? this.$t('CONVERSATION.REPLYBOX.CAMERA_UNAVAILABLE')
        : this.$t('CONVERSATION.REPLYBOX.RECORD_VIDEO');
    },
    locationTooltip() {
      return this.$t('CONVERSATION.REPLYBOX.LOCATION_BUTTON');
    },
    stickerTooltip() {
      return this.$t('CONVERSATION.REPLYBOX.STICKER_BUTTON');
    },
    preferredVideoCaptureMimeType() {
      return getPreferredVideoCaptureMimeType(this.allowedFileTypes);
    },
    microphoneStatusLabel() {
      if (this.audioInputDevices.length) {
        return '';
      }

      return this.hasCheckedAudioInputDevices
        ? this.$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_UNAVAILABLE')
        : this.$t('CONVERSATION.REPLYBOX.DEVICE_SETTINGS_LOADING');
    },
    cameraStatusLabel() {
      if (this.videoInputDevices.length) {
        return '';
      }

      return this.hasCheckedVideoInputDevices
        ? this.$t('CONVERSATION.REPLYBOX.CAMERA_UNAVAILABLE')
        : this.$t('CONVERSATION.REPLYBOX.DEVICE_SETTINGS_LOADING');
    },
    showAudioPlayStopButton() {
      if (this.isEditorDisabled) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      if (this.isOnPrivateNote) {
        return getAllowedFileTypesByChannel();
      }

      let channelType = this.channelType || this.inbox?.channel_type;
      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      if (this.isEditorDisabled) return false;
      return !this.isOnPrivateNote;
    },
    showPrimaryActionGroup() {
      return (
        !this.isEditorDisabled ||
        this.showAttachButton ||
        this.showAudioRecorderButton ||
        this.showCameraCaptureButton ||
        this.showVideoCaptureButton ||
        this.showAudioPlayStopButton
      );
    },
    showTemplateActionGroup() {
      return (
        this.enableWhatsAppTemplates ||
        this.enableWhatsAppFlows ||
        this.enableWhatsAppCommerce ||
        this.enableContentTemplates ||
        this.showStickerButton ||
        this.showLocationButton ||
        this.enableInsertArticleInReply
      );
    },
    showUtilityActionGroup() {
      return (
        this.showQuotedReplyToggle ||
        this.showMessageSignatureButton ||
        this.canShowDeviceSettingsButton ||
        ((this.isAWebWidgetInbox || this.isAPIInbox) &&
          !this.isOnPrivateNote &&
          !this.isEditorDisabled)
      );
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    toggleDeviceSettingsPanel() {
      this.showDeviceSettingsPanel = !this.showDeviceSettingsPanel;
    },
    closeDeviceSettingsPanel() {
      this.showDeviceSettingsPanel = false;
    },
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
  },
};
</script>

<template>
  <div
    class="chatmigo-reply-footer flex flex-col gap-2.5 p-3 sm:flex-row sm:items-end sm:justify-between"
    :class="wrapClass"
  >
    <div class="left-wrap chatmigo-reply-footer__left">
      <div
        v-if="showPrimaryActionGroup"
        class="action-group chatmigo-reply-footer__group chatmigo-reply-footer__group--primary"
      >
        <NextButton
          v-if="!isEditorDisabled"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
          icon="i-ph-smiley"
          slate
          faded
          sm
          @click="toggleEmojiPicker"
        />
        <div v-if="showAttachButton" class="action-with-hint">
          <FileUpload
            ref="uploadRef"
            v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            input-id="conversationAttachment"
            :size="4096 * 4096"
            :accept="allowedFileTypes"
            :multiple="enableMultipleFileUpload"
            :drop="enableDragAndDrop"
            :drop-directory="false"
            :data="{
              direct_upload_url: '/rails/active_storage/direct_uploads',
              direct_upload: true,
            }"
            @input-file="onFileUpload"
          >
            <NextButton
              v-if="showAttachButton"
              v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
              icon="i-ph-paperclip"
              slate
              faded
              sm
            />
          </FileUpload>
          <span class="action-hint">⌘⌥A</span>
        </div>
        <NextButton
          v-if="showAudioRecorderButton"
          v-tooltip.top-end="audioRecorderTooltip"
          :icon="!isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'"
          slate
          faded
          sm
          :disabled="audioRecorderDisabled"
          @click="toggleAudioRecorder"
        />
        <NextButton
          v-if="showCameraCaptureButton"
          v-tooltip.top-end="cameraCaptureTooltip"
          icon="i-ph-camera"
          slate
          faded
          sm
          :disabled="cameraCaptureDisabled"
          @click="onOpenCameraCapture"
        />
        <NextButton
          v-if="showVideoCaptureButton"
          v-tooltip.top-end="videoCaptureTooltip"
          icon="i-ph-video-camera"
          slate
          faded
          sm
          :disabled="videoCaptureDisabled"
          @click="onOpenVideoCapture(preferredVideoCaptureMimeType)"
        />
        <NextButton
          v-if="showAudioPlayStopButton"
          :icon="audioRecorderPlayStopIcon"
          slate
          faded
          sm
          :label="recordingAudioDurationText"
          @click="toggleAudioRecorderPlayPause"
        />
      </div>
      <div
        v-if="showPrimaryActionGroup && (showTemplateActionGroup || showUtilityActionGroup)"
        class="action-divider chatmigo-reply-footer__divider"
      />
      <div
        v-if="showTemplateActionGroup"
        class="action-group chatmigo-reply-footer__group chatmigo-reply-footer__group--templates"
      >
        <NextButton
          v-if="enableWhatsAppTemplates"
          v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="$emit('selectWhatsappTemplate')"
        />
        <NextButton
          v-if="enableContentTemplates"
          v-tooltip.top-end="$t('CONVERSATION.FOOTER.CONTENT_TEMPLATES')"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="$emit('selectContentTemplate')"
        />
        <NextButton
          v-if="enableWhatsAppFlows && !isEditorDisabled"
          v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_FLOWS')"
          icon="i-lucide-workflow"
          slate
          faded
          sm
          @click="$emit('selectWhatsappFlow')"
        />
        <NextButton
          v-if="enableWhatsAppCommerce && !isEditorDisabled"
          v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_COMMERCE')"
          icon="i-ph-shopping-bag-open"
          slate
          faded
          sm
          @click="$emit('selectWhatsappCommerce')"
        />
        <NextButton
          v-if="showStickerButton"
          v-tooltip.top-end="stickerTooltip"
          icon="i-ph-sticker"
          slate
          faded
          sm
          @click="onOpenStickerCatalog"
        />
        <NextButton
          v-if="showLocationButton"
          v-tooltip.top-end="locationTooltip"
          icon="i-ph-map-pin"
          slate
          faded
          sm
          @click="onOpenLocationMessage"
        />
        <NextButton
          v-if="enableInsertArticleInReply"
          v-tooltip.top-end="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
          icon="i-ph-article-ny-times"
          slate
          faded
          sm
          @click="toggleInsertArticle"
        />
      </div>
      <div
        v-if="showTemplateActionGroup && showUtilityActionGroup"
        class="action-divider chatmigo-reply-footer__divider"
      />
      <div
        v-if="showUtilityActionGroup"
        class="action-group chatmigo-reply-footer__group chatmigo-reply-footer__group--utility"
      >
        <NextButton
          v-if="showQuotedReplyToggle"
          v-tooltip.top-end="quotedReplyToggleTooltip"
          icon="i-ph-quotes"
          :variant="quotedReplyEnabled ? 'solid' : 'faded'"
          color="slate"
          sm
          :aria-pressed="quotedReplyEnabled"
          @click="$emit('toggleQuotedReply')"
        />
        <NextButton
          v-if="showMessageSignatureButton"
          v-tooltip.top-end="signatureToggleTooltip"
          icon="i-ph-signature"
          slate
          faded
          sm
          @click="toggleMessageSignature"
        />
        <div
          v-if="canShowDeviceSettingsButton"
          v-on-clickaway="closeDeviceSettingsPanel"
          class="device-settings-wrap"
        >
          <NextButton
            v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.DEVICE_SETTINGS_TOOLTIP')"
            icon="i-lucide-sliders-vertical"
            slate
            faded
            sm
            @click="toggleDeviceSettingsPanel"
          />
          <transition name="modal-fade">
            <div v-if="showDeviceSettingsPanel" class="device-settings-popover">
              <div class="device-settings-section">
                <label class="device-settings-label">
                  {{ $t('CONVERSATION.REPLYBOX.AUDIO_INPUT_DEVICE') }}
                </label>
                <select
                  v-if="audioInputDevices.length"
                  class="device-settings-select"
                  :value="selectedAudioInputDeviceId"
                  :aria-label="$t('CONVERSATION.REPLYBOX.AUDIO_INPUT_DEVICE')"
                  @change="onAudioInputDeviceChange($event.target.value)"
                >
                  <option
                    v-for="device in audioInputDevices"
                    :key="device.deviceId"
                    :value="device.deviceId"
                  >
                    {{ device.label }}
                  </option>
                </select>
                <p v-else class="device-settings-status">
                  {{ microphoneStatusLabel }}
                </p>
              </div>
              <div class="device-settings-section">
                <label class="device-settings-label">
                  {{ $t('CONVERSATION.REPLYBOX.CAMERA_INPUT_DEVICE') }}
                </label>
                <select
                  v-if="videoInputDevices.length"
                  class="device-settings-select"
                  :value="selectedVideoInputDeviceId"
                  :aria-label="$t('CONVERSATION.REPLYBOX.CAMERA_INPUT_DEVICE')"
                  @change="onVideoInputDeviceChange($event.target.value)"
                >
                  <option
                    v-for="device in videoInputDevices"
                    :key="device.deviceId"
                    :value="device.deviceId"
                  >
                    {{ device.label }}
                  </option>
                </select>
                <p v-else class="device-settings-status">
                  {{ cameraStatusLabel }}
                </p>
              </div>
            </div>
          </transition>
        </div>
        <VideoCallButton
          v-if="
            (isAWebWidgetInbox || isAPIInbox) &&
            !isOnPrivateNote &&
            !isEditorDisabled
          "
          :conversation-id="conversationId"
        />
      </div>
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-n-slate-12">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
    </div>
    <div class="right-wrap chatmigo-reply-footer__right">
      <div class="action-with-hint chatmigo-reply-footer__send">
        <NextButton
          :label="sendButtonText"
          type="submit"
          sm
          :color="isNote ? 'amber' : 'blue'"
          :disabled="isSendDisabled"
          class="flex-shrink-0"
          @click="onSend"
        />
        <span class="action-hint">↵</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply flex min-w-0 flex-1 flex-wrap items-center gap-2;
}

.action-group {
  @apply flex flex-wrap items-center gap-1.5;
}

.action-divider {
  @apply hidden self-stretch sm:block w-px bg-n-slate-4/80;
}

.action-with-hint {
  @apply relative flex items-center;
}

.action-hint {
  @apply pointer-events-none absolute -top-2.5 left-1/2 hidden -translate-x-1/2 rounded-md border border-n-weak bg-n-solid-1 px-1.5 py-0.5 text-[10px] font-semibold text-n-slate-11 shadow-sm;
}

.action-with-hint:hover .action-hint,
.action-with-hint:focus-within .action-hint {
  @apply inline-flex;
}

.device-settings-wrap {
  @apply relative;
}

.device-settings-popover {
  @apply absolute bottom-full right-0 z-[170] mb-2 flex w-[min(22rem,calc(100vw-1.5rem))] max-w-[22rem] flex-col gap-3 rounded-2xl border border-n-container bg-n-background p-3 shadow-[0px_18px_44px_0px_rgba(15,23,42,0.22)];
}

.device-settings-section {
  @apply flex flex-col gap-1.5;
}

.device-settings-label {
  @apply text-xs font-medium text-n-slate-11;
}

.device-settings-select {
  @apply h-9 rounded-lg border border-n-strong bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors;

  &:focus {
    @apply border-n-brand;
  }
}

.device-settings-status {
  @apply mb-0 text-xs text-n-slate-11;
}

.right-wrap {
  @apply flex w-full justify-end sm:w-auto sm:justify-start;
}

.chatmigo-reply-footer {
  @apply overflow-visible rounded-b-xl border-t border-n-weak;
  background: rgb(var(--surface-1) / 0.96);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.72),
    0 -1px 0 rgb(var(--border-container) / 0.7);
}

.chatmigo-reply-footer__left {
  @apply gap-2;
}

.chatmigo-reply-footer__group {
  @apply rounded-xl border border-n-weak px-2 py-1.5;
  background: rgb(var(--surface-2) / 0.88);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.55);
}

.chatmigo-reply-footer__group--primary {
  background: rgb(var(--surface-1));
}

.chatmigo-reply-footer__group--templates {
  background: rgb(var(--blue-3) / 0.34);
}

.chatmigo-reply-footer__group--utility {
  background: rgb(var(--slate-3) / 0.76);
}

.chatmigo-reply-footer__divider {
  @apply bg-n-slate-5/90;
}

.chatmigo-reply-footer__right {
  @apply items-center shrink-0;
}

.chatmigo-reply-footer__send {
  @apply rounded-xl border border-n-weak bg-n-surface-1 px-1.5 py-1.5;
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.68),
    0 10px 24px rgb(15 23 42 / 0.06);
}

:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}
</style>
